import { Check, X } from 'lucide-react';

interface PasswordStrengthProps {
  password: string;
  showRequirements?: boolean;
}

export function PasswordStrength({ password, showRequirements = true }: PasswordStrengthProps) {
  const requirements = [
    { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
    { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
    { label: 'One number', test: (p: string) => /\d/.test(p) },
  ];

  const passedCount = requirements.filter(req => req.test(password)).length;
  const strength = passedCount === 0 ? 0 : passedCount <= 2 ? 1 : passedCount === 3 ? 2 : 3;
  const strengthLabels = ['', 'Weak', 'Medium', 'Strong'];
  const strengthColors = ['', 'bg-red-500', 'bg-yellow-500', 'bg-green-500'];

  return (
    <div className="space-y-2">
      {password && (
        <div className="space-y-1">
          <div className="flex gap-1">
            {[1, 2, 3].map((level) => (
              <div
                key={level}
                className={`h-1 flex-1 rounded-full transition-all ${
                  level <= strength ? strengthColors[strength] : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
          {strength > 0 && (
            <p className={`text-xs font-medium ${
              strength === 1 ? 'text-red-600' : strength === 2 ? 'text-yellow-600' : 'text-green-600'
            }`}>
              Password strength: {strengthLabels[strength]}
            </p>
          )}
        </div>
      )}

      {showRequirements && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Password must contain:</p>
          {requirements.map((req, index) => {
            const passed = req.test(password);
            return (
              <div key={index} className="flex items-center gap-2">
                {passed ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-gray-400" />
                )}
                <span className={`text-xs ${passed ? 'text-green-600' : 'text-gray-600'}`}>
                  {req.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
